import { supabase } from './supabase'

/**
 * Send an SMS message via the send-sms edge function
 */
export async function sendSms({ message, recipientUserId, companyId }) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ message, recipientUserId, companyId }),
    }
  )

  const result = await response.json()

  if (!response.ok || !result.success) {
    throw new Error(result.error?.message || 'Failed to send SMS')
  }

  return result.data
}

/**
 * Get SMS credit balance for a company
 */
export async function getSmsCredits(companyId) {
  const { data, error } = await supabase
    .from('sms_credits')
    .select('balance, lifetime_purchased, lifetime_used, lifetime_free')
    .eq('company_id', companyId)
    .single()

  if (error) throw error
  return data
}

/**
 * Get paginated SMS send log for a company
 */
export async function getSmsSendLog(companyId, { page = 1, pageSize = 20 } = {}) {
  const offset = (page - 1) * pageSize

  const { data, error, count } = await supabase
    .from('sms_send_log')
    .select(`
      id, recipient_phone, recipient_name, message_content, sender_id,
      credits_used, auth_method, status, error_message, created_date,
      sent_by_profile:user_profiles!sms_send_log_sent_by_fkey(display_name),
      recipient_profile:user_profiles!sms_send_log_recipient_user_id_fkey(display_name)
    `, { count: 'exact' })
    .eq('company_id', companyId)
    .order('created_date', { ascending: false })
    .range(offset, offset + pageSize - 1)

  if (error) throw error
  return { data: data || [], total: count || 0, page, pageSize }
}

/**
 * Get paginated SMS credit transaction history for a company
 */
export async function getSmsCreditTransactions(companyId, { page = 1, pageSize = 20 } = {}) {
  const offset = (page - 1) * pageSize

  const { data, error, count } = await supabase
    .from('sms_credit_transactions')
    .select(`
      id, type, amount, balance_after, description, created_date,
      created_by_profile:user_profiles!sms_credit_transactions_created_by_fkey(display_name)
    `, { count: 'exact' })
    .eq('company_id', companyId)
    .order('created_date', { ascending: false })
    .range(offset, offset + pageSize - 1)

  if (error) throw error
  return { data: data || [], total: count || 0, page, pageSize }
}
